import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNotification } from "../pages/notification/services/getNotification";
import { getUserProfile } from "./getUser";
import { setActiveAccount as setActiveAccountAction } from "../store/actions/accountAction";

const buildMeterNumbers = (pick) => ({
  water: pick?.waterMeterId || pick?.estateId?.generalMeter?.water || null,
  gas: pick?.gasMeterId || pick?.estateId?.generalMeter?.gas || null,
  electricity:
    pick?.electricityMeterId ||
    pick?.estateId?.generalMeter?.electricity ||
    null,
});

const buildAccountFromEstate = (pick) => {
  const resolvedEstate = pick?.estateId || pick;
  return {
    type: "estate",
    estateId: resolvedEstate?._id || resolvedEstate,
    name:
      resolvedEstate?.estateName ||
      resolvedEstate?.name ||
      pick?.estateName ||
      pick?.name ||
      String(resolvedEstate?._id || resolvedEstate || "Estate"),
    isPrimary: !!(pick?.isPrimary || pick?.estateId?.isPrimary),
    meterNumbers: buildMeterNumbers(pick),
    paymentMode: resolvedEstate?.paymentSettings?.paymentMode || "PAYSTACK",
    bankTransfer: resolvedEstate?.paymentSettings?.bankTransfer || null,
  };
};

// ---------------------------------------------------------------------------

export const UserProfileContext = createContext({});

export const UserProfileContextProvider = ({ children }) => {
  const userId = useSelector((state) => state?.auth?.userId);
  const activeAccount = useSelector((state) => state?.account?.activeAccount);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState({});
  const [notifications, setNotifications] = useState([]);
  const dispatch = useDispatch();

  const activeAccountRef = useRef(activeAccount);
  useEffect(() => {
    activeAccountRef.current = activeAccount;
  }, [activeAccount]);

  const setActiveAccount = useCallback(
    (acc) => dispatch(setActiveAccountAction(acc)),
    [dispatch],
  );

  useEffect(() => {
    getUserProfile(userId, setUserProfile, setIsLoading);
    getNotification(setNotifications, () => {});
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const timer = setInterval(() => {
      getNotification(setNotifications, () => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [userId]);

  useEffect(() => {
    if (isLoading || !userProfile) return;

    const estates = Array.isArray(userProfile?.estates)
      ? userProfile.estates
      : [];
    if (estates.length === 0) return;

    const current = activeAccountRef.current;

    if (!current) {
      // First time — pick the primary estate (or the only estate)
      const primary = estates.find(
        (e) => !!e?.isPrimary || !!e?.estateId?.isPrimary,
      );
      const pick = primary || (estates.length === 1 ? estates[0] : null);
      if (pick) {
        dispatch(setActiveAccountAction(buildAccountFromEstate(pick)));
      }
    } else {
      // Already selected — find the matching estate entry and refresh meters
      const match = estates.find(
        (e) =>
          String(e?.estateId?._id || e?.estateId) === String(current.estateId),
      );
      if (match) {
        dispatch(
          setActiveAccountAction({
            ...current,
            meterNumbers: buildMeterNumbers(match),
            paymentMode: match?.estateId?.paymentSettings?.paymentMode || "PAYSTACK",
            bankTransfer: match?.estateId?.paymentSettings?.bankTransfer || null,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, userProfile, dispatch]);

  const refreshProfile = async () => {
    setIsLoading(true);
    const res = await getUserProfile(userId, setUserProfile, setIsLoading);
    return res?.data?.userDetails?.data?.users?.[0] ?? null;
  };

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        isLoading,
        notifications,
        activeAccount,
        setActiveAccount,
        refreshProfile,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};
